import * as s from "./SignupStyled";
import * as cs from "../SignCommonStyled";
import TITLE_MYDW_ICON from "assets/icons/title_myDW.svg";
import { Spin } from "antd";
import {
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import InputInfo from "./step/InputInfo";
import ExpiredToken from "./step/ExpiredToken";
import ResetPassword from "./step/ResetPassword";
import Success from "./step/Success";
import { useNoticeHeight } from "components/NoticePovider";
import { MYDW_LOGO } from "components/blocks/sidenav/TopInfo";
import Reseller from "./step/Reseller";
import {
  SIGNUP_STEP,
  SIGNUP_STEP_TITLE,
  SIGNUP_UPDATE_TYPE,
  SignupUserInfo,
} from "api/interfaces/authInterface.interface";
import { susccessSteps } from "utils/AuthUtil";
import FreeTrial from "./step/FreeTrial";
import Spinner from "components/atoms/loader/Spinner";

interface Props {
  step: SIGNUP_STEP;
  checkedEmail: string;
  onClickVerify: () => void;
  onClickLogin: () => void;
  onClickVerifyCancel: () => void;
  onChangeUserInfo: (type: SIGNUP_UPDATE_TYPE, data: string | boolean) => void;
  userInfo: SignupUserInfo;
  verifyCode: string;
  onChangeVerifyCode: (value: string) => void;
  isValidPassword: boolean;
  setIsValidPassword: React.Dispatch<React.SetStateAction<boolean>>;
  onClickBackLogin: () => void;
  firstNameRef: React.RefObject<HTMLInputElement>;
  lastNameRef: React.RefObject<HTMLInputElement>;
  passwordRef: React.RefObject<HTMLInputElement>;
  confirmPasswordRef: React.RefObject<HTMLInputElement>;
  agreementRef: React.RefObject<HTMLInputElement>;
  validateMsg: string;
  onClickResend: () => void;
  isValidVerify: boolean;
  isLoadedToken: boolean;
  isExpiredToken: boolean;
  cloudLoading: boolean;
  isPhoneVerified: boolean;
  onSendVerify: () => void;
  mfaNumber: string;
  validateInputMsg: ValidateMsg;
  setIsValidated: React.Dispatch<React.SetStateAction<Validate>>;
  setValidateInputMsg: React.Dispatch<React.SetStateAction<ValidateMsg>>;
  setUserInfo: React.Dispatch<React.SetStateAction<SignupUserInfo>>;
  isVerifyPhoneNumber: boolean;
  setValidateMsg: React.Dispatch<React.SetStateAction<string>>;
  setStep: React.Dispatch<React.SetStateAction<SIGNUP_STEP>>;
  setIsValidVerify: React.Dispatch<React.SetStateAction<boolean>>;
  setIsResend: React.Dispatch<React.SetStateAction<boolean>>;
  isResend: boolean;
  setCheckedEmail: React.Dispatch<React.SetStateAction<string>>;
}

export function SignupPagePresenter(props: Props): JSX.Element {
  const {
    step,
    checkedEmail,
    setIsValidated,
    setValidateInputMsg,
    setUserInfo,
    isVerifyPhoneNumber,
    setValidateMsg,
    setStep,
    setIsValidVerify,
    onClickVerify,
    onClickLogin,
    onClickVerifyCancel,
    onChangeUserInfo,
    userInfo,
    verifyCode,
    onChangeVerifyCode,
    isValidPassword,
    setIsValidPassword,
    onClickBackLogin,
    firstNameRef,
    lastNameRef,
    passwordRef,
    confirmPasswordRef,
    agreementRef,
    validateMsg,
    onClickResend,
    isValidVerify,
    isLoadedToken,
    isExpiredToken,
    cloudLoading,
    isPhoneVerified,
    onSendVerify,
    mfaNumber,
    validateInputMsg,
    setCheckedEmail,
    setIsResend,
    isResend,
  } = props;

  const { noticeHeight } = useNoticeHeight();
  const title = SIGNUP_STEP_TITLE[step];
  const isSuccessStep = susccessSteps.includes(step);
  const isResellerStep =
    step === SIGNUP_STEP.RESELLER_SIGNUP ||
    step === SIGNUP_STEP.RESELLER_SUBMISSION;
  const shouldShowOutsideLogo =
    isSuccessStep || (isExpiredToken && step === SIGNUP_STEP.EXPIRED_TOKEN) || step === SIGNUP_STEP.RESET_PASSWORD;
  const shouldShowEmailBadge = !isSuccessStep && Boolean(checkedEmail);

  const content = (() => {
    if (isSuccessStep) {
      return (
        <Success
          userInfo={userInfo}
          onClickLogin={onClickLogin}
          step={step}
          checkedEmail={checkedEmail}
          isResend={isResend}
        />
      );
    }
    if (step === SIGNUP_STEP.FREE_TRIAL) {
      return (
        <FreeTrial
          validateInputMsg={validateInputMsg}
          userInfo={userInfo}
          onChangeUserInfo={onChangeUserInfo}
          setStep={setStep}
          setIsValidated={setIsValidated}
          setIsResend={setIsResend}
          isResend={isResend}
          validateMsg={validateMsg}
          setValidateMsg={setValidateMsg}
          setValidateInputMsg={setValidateInputMsg}
          setCheckedEmail={setCheckedEmail}
          onClickBackLogin={onClickBackLogin}
        />
      );
    }

    if (isResellerStep) {
      return (
        <Reseller
          onChangeUserInfo={onChangeUserInfo}
          userInfo={userInfo}
          checkedEmail={checkedEmail}
          onClickVerify={onClickVerify}
          onClickVerifyCancel={onClickVerifyCancel}
          verifyCode={verifyCode}
          onChangeVerifyCode={onChangeVerifyCode}
          passwordRef={passwordRef}
          confirmPasswordRef={confirmPasswordRef}
          validateMsg={validateMsg}
          onClickResend={onClickResend}
          isValidVerify={isValidVerify}
          isPhoneVerified={isPhoneVerified}
          onSendVerify={onSendVerify}
          mfaNumber={mfaNumber}
          validateInputMsg={validateInputMsg}
          step={step}
          setStep={setStep}
          setIsValidated={setIsValidated}
          setValidateInputMsg={setValidateInputMsg}
          setValidateMsg={setValidateMsg}
          isVerifyPhoneNumber={isVerifyPhoneNumber}
        />
      );
    }

    if (!isLoadedToken) {
      return <Spinner />;
    }

    if (step === SIGNUP_STEP.RESET_PASSWORD) {
      return (
        <ResetPassword
          userInfo={userInfo}
          onChangeUserInfo={onChangeUserInfo}
          isValidPassword={isValidPassword}
          isValidConfirm={Boolean(validateInputMsg.confirmPassword)}
          onClickBackLogin={onClickBackLogin}
          passwordRef={passwordRef}
          confirmPasswordRef={confirmPasswordRef}
          validateMsg={validateMsg}
          isExpiredToken={isExpiredToken}
          validateInputMsg={validateInputMsg}
          setStep={setStep}
          setIsValidPassword={setIsValidPassword}
          setValidateMsg={setValidateMsg}
          setIsValidated={setIsValidated}
          checkedEmail={checkedEmail}
        />
      );
    }

    if (isExpiredToken && step === SIGNUP_STEP.EXPIRED_TOKEN) {
      return <ExpiredToken validateMsg={validateMsg} />;
    }

    if (step === SIGNUP_STEP.INPUT_INFO) {
      return (
        <InputInfo
          checkedEmail={checkedEmail}
          userInfo={userInfo}
          verifyCode={verifyCode}
          onChangeVerifyCode={onChangeVerifyCode}
          onClickVerify={onClickVerify}
          onClickVerifyCancel={onClickVerifyCancel}
          onChangeUserInfo={onChangeUserInfo}
          firstNameRef={firstNameRef}
          lastNameRef={lastNameRef}
          passwordRef={passwordRef}
          confirmPasswordRef={confirmPasswordRef}
          agreementRef={agreementRef}
          validateMsg={validateMsg}
          onClickResend={onClickResend}
          isValidVerify={isValidVerify}
          isPhoneVerified={isPhoneVerified}
          onSendVerify={onSendVerify}
          mfaNumber={mfaNumber}
          validateInputMsg={validateInputMsg}
          setIsValidated={setIsValidated}
          setValidateInputMsg={setValidateInputMsg}
          setUserInfo={setUserInfo}
          isVerifyPhoneNumber={isVerifyPhoneNumber}
          setValidateMsg={setValidateMsg}
          setStep={setStep}
          setIsValidVerify={setIsValidVerify}
        />
      );
    }

    return null;
  })();

  return (
    <Spin
      spinning={cloudLoading}
      size="large"
      indicator={
        <div className="progress-indicator progress-indicator--responsive" />
      }
    >
      <cs.BG noticeHeight={noticeHeight}>
        <cs.Container
          className={shouldShowOutsideLogo ? "signup-container" : ""}
        >
          {shouldShowOutsideLogo && (
            <s.TitleWrapper>
              <s.SignupTitle>
                WELCOME TO
                <img src={TITLE_MYDW_ICON} width="80" alt="MyDW Icon" />
              </s.SignupTitle>
              <p className="txt">Complete Surveillance Solutions</p>
            </s.TitleWrapper>
          )}
          <cs.LoginBox>
            {!shouldShowOutsideLogo && (
              <button onClick={onClickBackLogin}>
                <img
                  src={MYDW_LOGO}
                  width="120"
                  alt="MyDW Icon"
                  className="mydw-logo"
                />
              </button>
            )}
            {title !== "" && <div className="title">{title}</div>}
            {shouldShowEmailBadge && (
              <cs.EmailBadge className="email-badge">
                {checkedEmail}
              </cs.EmailBadge>
            )}
            {content}
          </cs.LoginBox>
        </cs.Container>
      </cs.BG>
    </Spin>
  );
}
